'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Pencil, Trash2, UserPlus, Loader2, Users, ShieldCheck, BadgeCheck, Wallet } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import { PageShell } from '@/components/ui/page-shell'
import { EmployeeProfile } from './EmployeeProfile'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

interface User {
  id: string
  name: string
  email: string
  role: string
  employeeId: string | null
  department: string | null
  position: string | null
  phone: string | null
  isActive: boolean
  isStipended?: boolean
  monthlyCap?: number | null
  joinDate?: string | null
  assignedHrId?: string | null
  assignedHrName?: string | null
  createdAt: string
}

/** Sentinel for "no assigned HR" — Radix Select rejects an empty item value. */
const NO_HR = 'none'

/** Roles paid through the stipend workflow, so they get the stipend fields. */
const STIPENDED_ROLES = ['EMPLOYEE', 'INTERN']

function roleTone(role: string): Parameters<typeof StatusPill>[0]['tone'] {
  if (role === 'ADMIN') return 'orange'
  if (role === 'HR') return 'violet'
  if (role === 'INTERN') return 'cyan'
  return 'blue'
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  // When set, the profile drill-down replaces the user list.
  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    employeeId: '',
    department: '',
    position: '',
    phone: '',
    isStipended: true,
    monthlyCap: '',
    joinDate: '',
    isActive: true,
    assignedHrId: NO_HR,
  })
  const [submitting, setSubmitting] = useState(false)

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/users')
      const data = await res.json()
      if (data.users) {
        setUsers(data.users)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Inactive employees are managed from Settings and never appear in the active directory.
  const filteredUsers = useMemo(() => users.filter(u => u.isActive !== false), [users])

  const stats = useMemo(() => {
    const total = filteredUsers.length
    const active = filteredUsers.filter(u => u.isActive).length
    const admins = filteredUsers.filter(u => u.role === 'ADMIN').length
    const stipended = filteredUsers.filter(u => STIPENDED_ROLES.includes(u.role) && u.isStipended !== false).length
    return { total, active, admins, stipended }
  }, [filteredUsers])

  // Only ADMIN/HR accounts can be somebody's HR contact — the API enforces the
  // same rule, so offering anyone else here would just produce a 400.
  const hrOptions = useMemo(
    () => users.filter(u => (u.role === 'HR' || u.role === 'ADMIN') && u.isActive !== false && u.id !== editUser?.id),
    [users, editUser]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const payload = {
      ...formData,
      password: formData.password || undefined,
      monthlyCap: formData.monthlyCap === '' ? null : Number(formData.monthlyCap),
      joinDate: formData.joinDate || null,
      assignedHrId: formData.assignedHrId === NO_HR ? null : formData.assignedHrId,
    }

    try {
      if (editUser) {
        await apiFetch(`/api/users/${editUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        toast.success('User updated successfully')
        fetchUsers()
        setDialogOpen(false)
        setEditUser(null)
        resetForm()
      } else {
        await apiFetch('/api/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        toast.success('User created successfully')
        fetchUsers()
        setDialogOpen(false)
        resetForm()
      }
    } catch (e: any) {
      toast.error(e.message || (editUser ? 'Failed to update user' : 'Failed to create user'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return

    try {
      await apiFetch(`/api/users/${userId}`, {
        method: 'DELETE'
      })
      toast.success('User deleted successfully')
      fetchUsers()
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete user')
    }
  }

  const openEditDialog = (user: User) => {
    setEditUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      employeeId: user.employeeId || '',
      department: user.department || '',
      position: user.position || '',
      phone: user.phone || '',
      isStipended: user.isStipended !== false,
      monthlyCap: user.monthlyCap != null ? String(user.monthlyCap) : '',
      joinDate: user.joinDate || '',
      isActive: user.isActive !== false,
      assignedHrId: user.assignedHrId || NO_HR,
    })
    setDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE',
      employeeId: '',
      department: '',
      position: '',
      phone: '',
      isStipended: true,
      monthlyCap: '',
      joinDate: '',
      isActive: true,
      assignedHrId: NO_HR,
    })
  }

  const addButton = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open)
      if (!open) {
        setEditUser(null)
        resetForm()
      }
    }}>
      <DialogTrigger asChild>
        <Button className={PRIMARY_BTN}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10">
        <DialogHeader>
          <DialogTitle className="text-neutral-900 dark:text-white">{editUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          <DialogDescription className="text-neutral-600 dark:text-white/60">
            {editUser ? 'Update user information' : 'Create a new user account'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-neutral-800 dark:text-white/80">Full Name</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-800 dark:text-white/80">Email</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-neutral-800 dark:text-white/80">
                Password {editUser && <span className="text-neutral-400 dark:text-white/40">(leave blank to keep)</span>}
              </Label>
              <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editUser} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-neutral-800 dark:text-white/80">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMPLOYEE">Employee</SelectItem>
                  <SelectItem value="INTERN">Intern</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="HR">HR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status" className="text-neutral-800 dark:text-white/80">Status</Label>
              <Select value={formData.isActive ? 'Active' : 'Inactive'} onValueChange={(value) => setFormData({ ...formData, isActive: value === 'Active' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employeeId" className="text-neutral-800 dark:text-white/80">Employee ID</Label>
              <Input id="employeeId" value={formData.employeeId} onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })} placeholder="e.g., EMP001" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="department" className="text-neutral-800 dark:text-white/80">Department</Label>
              <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Web">Web</SelectItem>
                  <SelectItem value="AR/VR">AR/VR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assignedHrId" className="text-neutral-800 dark:text-white/80">Assigned HR</Label>
              <Select value={formData.assignedHrId} onValueChange={(value) => setFormData({ ...formData, assignedHrId: value })}>
                <SelectTrigger><SelectValue placeholder="Select HR" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_HR}>Not assigned</SelectItem>
                  {hrOptions.map(hr => (
                    <SelectItem key={hr.id} value={hr.id}>
                      {hr.name} ({hr.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="position" className="text-neutral-800 dark:text-white/80">Position</Label>
              <Input id="position" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-neutral-800 dark:text-white/80">Phone</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>
          </div>
          {STIPENDED_ROLES.includes(formData.role) && (
            <div className="rounded-xl border border-neutral-200 dark:border-white/10 p-3 space-y-3 bg-neutral-50/50 dark:bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isStipended"
                  checked={formData.isStipended}
                  onCheckedChange={(checked) => setFormData({ ...formData, isStipended: checked === true })}
                />
                <Label htmlFor="isStipended" className="text-neutral-800 dark:text-white/80 cursor-pointer">
                  Stipended intern (pay calculations run during monthly generation)
                </Label>
              </div>
              <p className="text-xs text-neutral-400 dark:text-white/40">
                Uncheck for unpaid trial period (typically first ~3 months). Monthly report generation will skip them and avoid AI cost.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="monthlyCap" className="text-neutral-800 dark:text-white/80">Monthly Stipend Cap (₹)</Label>
                  <Input
                    id="monthlyCap"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="No cap"
                    value={formData.monthlyCap}
                    onChange={(e) => setFormData({ ...formData, monthlyCap: e.target.value })}
                    disabled={!formData.isStipended}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="joinDate" className="text-neutral-800 dark:text-white/80">Join Date</Label>
                  <Input
                    id="joinDate"
                    type="date"
                    value={formData.joinDate}
                    onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                  />
                  <p className="text-[11px] text-neutral-400 dark:text-white/40">Used to prorate expected hours when joining mid-month.</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={submitting} className={PRIMARY_BTN}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {editUser ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editUser ? 'Update User' : 'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )

  if (profileUser) {
    return (
      <PageShell>
        <EmployeeProfile user={profileUser} onBack={() => setProfileUser(null)} />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <SectionHeader
        title="User Management"
        description="Manage admin and employee accounts"
        icon={Users}
        action={addButton}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiTile label="Total Users" value={stats.total} icon={Users} tone="orange" />
        <KpiTile label="Active" value={stats.active} icon={BadgeCheck} tone="green" />
        <KpiTile label="Admins" value={stats.admins} icon={ShieldCheck} tone="violet" />
        <KpiTile label="Stipended" value={stats.stipended} icon={Wallet} tone="cyan" />
      </div>

      <GlassCard inset>
        {loading ? (
          <TabLoader />
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No users found"
              description="Add a user to populate the directory."
            />
          ) : (
            <>
              {/* Mobile card stack */}
              <div className="md:hidden p-3 space-y-3">
                {filteredUsers.map((user) => (
                <GlassCard key={user.id} tone={user.isActive ? 'green' : 'red'} interactive className="!p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setProfileUser(user)}
                          className="text-neutral-900 dark:text-white/90 font-medium truncate text-left hover:text-orange-500 dark:hover:text-orange-400 hover:underline underline-offset-2 transition-colors"
                        >
                          {user.name}
                        </button>
                        <StatusPill tone={roleTone(user.role)}>{user.role}</StatusPill>
                      </div>
                      <p className="text-xs text-neutral-600 dark:text-white/60 truncate">{user.email}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-[11px] font-mono text-neutral-400 dark:text-white/40">{user.employeeId || '—'}</span>
                        {user.department && <span className="text-[11px] text-neutral-600 dark:text-white/60">· {user.department}</span>}
                        {user.assignedHrName && <span className="text-[11px] text-neutral-600 dark:text-white/60">· HR: {user.assignedHrName}</span>}
                        <StatusPill tone={user.isActive ? 'green' : 'red'} dot>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </StatusPill>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} className="text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white h-8 w-8">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200 h-8 w-8">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <ScrollArea className="h-[560px]">
                <div className="min-w-[700px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-400 dark:text-white/40">Employee ID</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Name</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Email</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Role</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Department</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Assigned HR</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40">Status</TableHead>
                        <TableHead className="text-neutral-400 dark:text-white/40 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:bg-white/[0.03]">
                          <TableCell className="text-neutral-900 dark:text-white/90 font-mono">{user.employeeId || '-'}</TableCell>
                          <TableCell>
                            <button
                              type="button"
                              onClick={() => setProfileUser(user)}
                              className="text-left font-medium text-neutral-900 dark:text-white/90 hover:text-orange-500 dark:hover:text-orange-400 hover:underline underline-offset-2 transition-colors"
                              title={`View ${user.name}'s profile`}
                            >
                              {user.name}
                            </button>
                          </TableCell>
                          <TableCell className="text-neutral-700 dark:text-white/70">{user.email}</TableCell>
                          <TableCell>
                            <StatusPill tone={roleTone(user.role)}>{user.role}</StatusPill>
                          </TableCell>
                          <TableCell className="text-neutral-700 dark:text-white/70">{user.department || '-'}</TableCell>
                          <TableCell className="text-neutral-700 dark:text-white/70">{user.assignedHrName || '-'}</TableCell>
                          <TableCell>
                            <StatusPill tone={user.isActive ? 'green' : 'red'} dot>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </StatusPill>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} className="text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>
          </>
        )}
      </GlassCard>
    </PageShell>
  )
}
